import type { BrandingConfig } from './types';

export type BrandingEnv = Partial<ImportMetaEnv> & {
  readonly VITE_AX_PRODUCT_NAME?: string;
  readonly VITE_AX_TITLE?: string;
  readonly VITE_AX_COMPANY_NAME?: string;
  readonly VITE_AX_COPYRIGHT_TEXT?: string;
  /** 라이트 모드 로고 경로. 예: /branding/logo-light.svg */
  readonly VITE_AX_LOGO_LIGHT?: string;
  /** 로그인 배경 이미지 경로. 예: /branding/login-bg.jpg */
  readonly VITE_AX_LOGIN_BG?: string;
};

function readEnvString(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : undefined;
}

/**
 * FE 환경설정 값을 BrandingConfig 로 병합한다.
 *
 * 우선순위: VITE_AX_* 환경변수 > createAxApp(options.branding) > defaultBranding.
 * 컴포넌트는 계속 useBranding() 만 사용하므로 브랜딩 참조 경로는 하나로 유지된다.
 */
export function resolveBrandingConfig(
  base: BrandingConfig,
  env: BrandingEnv = import.meta.env,
): BrandingConfig {
  const productName = readEnvString(env.VITE_AX_PRODUCT_NAME);
  const explicitTitle = readEnvString(env.VITE_AX_TITLE);
  const companyName = readEnvString(env.VITE_AX_COMPANY_NAME);
  const copyrightText = readEnvString(env.VITE_AX_COPYRIGHT_TEXT);
  const logoLight = readEnvString(env.VITE_AX_LOGO_LIGHT);
  const loginBg = readEnvString(env.VITE_AX_LOGIN_BG);

  let resolved: BrandingConfig = { ...base };

  if (productName) {
    resolved = { ...resolved, productName };
  }

  if (explicitTitle) {
    resolved = { ...resolved, title: explicitTitle };
  } else if (productName && base.title === base.productName) {
    resolved = { ...resolved, title: productName };
  }

  if (companyName) {
    resolved = { ...resolved, companyName };
  }

  if (copyrightText) {
    resolved = { ...resolved, copyrightText };
  }

  if (logoLight || loginBg) {
    resolved = {
      ...resolved,
      logo: {
        ...resolved.logo,
        ...(logoLight ? { light: logoLight } : {}),
        ...(loginBg ? { loginBackground: loginBg } : {}),
      },
    };
  }

  return resolved;
}
